import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { loadRentalAnalytics, loadRentalInfo } from "../interfaces/rentInterface";
import { useDispatch, useSelector } from "react-redux";
import { clearAgreementInfo, clearRentInfo } from "../features/rentSlice";
import { Card, Row, Col, Tag, Image, Button, Select , message } from "antd";
import ActionsMenu from "./atoms/ActionsMenu";
import defaultImage from "../assets/default.png";
import Heading from "./atoms/Heading";
import KeyValue from "./atoms/KeyValue";
import TableView from "./atoms/TableView";
import { checkEqual, isoDateToDate, safeParse, transformArray, transformValues } from "../helpers/helper";
import CardGridView from "./atoms/CardGridView";
import { BackwardOutlined , DeleteOutlined , FilePdfOutlined , SettingOutlined } from "@ant-design/icons";
import { UpOutlined, DownOutlined , CalendarOutlined,PlusOutlined} from "@ant-design/icons";
import { deleteAgreement, loadAgreements } from "../interfaces/agreementInterface";
import { loadInvoices } from "../interfaces/invoiceInterface";
import Filters from "./filters/filters";
import PersistantRef from "./atoms/PersistantRef";
import { setFormValue } from "../features/formSlice";
import { FormNavigator } from "../dynamicform/formNavigator";
import { getIcon } from "./atoms/FuzzyIcon";
import resolvers from "../helpers/resolvers";
import GalleryBlock from "./atoms/GalleryBlock";
import FlexibleCard from "./atoms/FlexibleCard";
import DynamicDashboard from "./analytics/DynamicDashboard";
import CenteredDisplay, { Loader } from "./atoms/CenteredContent";
import { generateInvoice } from "../interfaces/invoiceBuilderInterface";
import { currencyFmt } from "./invoicebuilder/components/preprocessInvoice";

const imageStyle = {
  padding: 5,
};
const cardStyle = {
  margin: 10,
};
const tabList = [
  {
    key: "agreements",
    label: <KeyValue value={"Agreements"} />,
  },
];

const RentalView = () => {
  var dispatch = useDispatch();
  const constants = useSelector(state=>state.workspace.constants)
  const invoiceConfigs = useSelector(state=>state.workspace.invoiceConfigs)
  const [activeTab, setActiveTab] = useState("agreements");
  const { id } = useParams();
  const rentInfo = useSelector((state) => state.rent.rentalInfo);
  const invoicesInfo = useSelector((state) => state.rent.invoiceInfo);
  const pageLimit = useSelector((state) => state.rent.pageLimit);
  const agreementInfo = useSelector(
    (state) => state.rent.agreementInfo[state.rent.agreementInfo.currentPage]
  );
  const shouldShowAnalytics = useSelector((state)=>state.workspace?.role?.sensitiveInfoBlock?.analytics || false)
  const defaultInvoiceConfig = useSelector(state=>state.workspace.defaultInvoiceConfig)
  const [selectedInvoiceConfig, setSelectedInvoiceConfig] = useState(defaultInvoiceConfig)
  const analyticsDatasource = useSelector(state=>state.analytics.perrental?.[id]?.['data'])
  const agreementFilters = useSelector((state) => state.rent.agreementFilters);
  const defaultCurrency = useSelector((state)=>state.workspace.constants.DEFAULT_CURRENCY)
  const previousAgreementFilters = new PersistantRef(
    "previousAgreementFilters"
  );
  const formNavigator = FormNavigator(dispatch)
  const navigate = useNavigate()
  useEffect(() => {
    if (!checkEqual(agreementFilters, previousAgreementFilters.getValue())) {
      dispatch(clearAgreementInfo());
      loadAgreements(dispatch, 1, pageLimit, {}, id, agreementFilters);
      previousAgreementFilters.setValue(agreementFilters);
    }
  }, [agreementFilters]);
  const agreementPages = useSelector(
    (state) => new Set(Object.keys(state.rent.agreementInfo))
  );
  const agreementCount = useSelector(
    (state) => state.rent.agreementInfo["totalData"]
  );
  useEffect(() => {
      if (!defaultInvoiceConfig && invoiceConfigs && invoiceConfigs.length > 0){
        setSelectedInvoiceConfig(invoiceConfigs?.[0]?.['id'])
      }
    }, [invoiceConfigs]);
  useEffect(() => {
    if (id) {
      dispatch(clearRentInfo());
      loadRentalInfo(dispatch, id);
      if(shouldShowAnalytics){
        loadRentalAnalytics(dispatch,id)
      }
    }
  }, [id,shouldShowAnalytics]);
  const config = {
    rentFields: [
      "name",
      "type",
      "createdAt",
      "buyDate",
      "buyValue",
      "totalUnits",
      "description",
      "defaultAmount__Default Rental Amount"
    ],
    longRentFields: new Set(["description"]),
    tenantFields: ["name", "email", "phone", "address","archived__Archived"],
    longTenantFields: new Set(["address"]),
    agreementFields: [
      "email",
      "startDate",
      "quantity",
      "endDate",
      "amount",
      "status",


    ],
    invoiceFields: [
      "id",
      "periodStart",
      "periodEnd",
      "dueDate",
      "status",
      "amount",
    ],
    agreementFieldsRender: {
      startDate: (value) => {
        const addTime = rentInfo.quantibleType == 'PERMANENT'
        return isoDateToDate(value,addTime);
      },
      endDate: (value) => {
        const addTime = rentInfo.quantibleType == 'PERMANENT'
        return isoDateToDate(value,addTime);
      },
      email:(value,record)=>{
        return record.rentalAgreement.tenant.email
      },
      amount:(value,_)=>{
      return currencyFmt(value,defaultCurrency)
    },
      status: (value, record) => {
        return (
          <Tag
            style={{ fontSize: "1rem", padding: 2 }}
            color={record.rentalAgreement.status == "Active" ? "#87d068" : "#f50"}
          >
            {record.rentalAgreement.status}
          </Tag>
        );
      },
    },

    agreementRowExpand: (record) => {
      return (
        <>
          <CardGridView
            heading={"Tenant Info"}
            info={record.rentalAgreement.tenant}
            fields={config.tenantFields}
            longFields={config.longTenantFields}
            foldable={true}
          />

        </>
      );
    },
  };
if (Object.keys(rentInfo).length == 0){
    return (<Loader/>)
  }
  return (
    <div style={{ height: "90vh" }}>
      <FlexibleCard
        compact={true}
        extras={
          <Button
            size="small"
            type="primary"
            style={{ cursor: "pointer", float: "right" }}
            onClick={() => {
              formNavigator.rental(id);
            }}
          >
            Edit
          </Button>
        }
        cardTitle={constants["PRODUCT_INFO"] ?? "Rental Info & Images"}

        // you can also use: subtitle="" or extras={} if you want later
      >
        <Link to={"/rentals"}>
          <Button
            style={{
              padding: 6,
              height: 20,
              position: "absolute",
              left: -5,
              top: -5,
            }}
            variant="filled"
            color="danger"
          >
            <BackwardOutlined title="back" />
          </Button>
        </Link>

        <Row>
          <Col span={16}>
            <CardGridView
              longFields={config.longRentFields}
              fields={config.rentFields}
              info={rentInfo}
            />
          </Col>

          <Col span={8}>
            <GalleryBlock
              images={rentInfo?.images?.length && safeParse(rentInfo.images).length>0   ? safeParse(rentInfo.images) : [
                defaultImage,
                defaultImage,
                defaultImage,
                defaultImage,
                defaultImage,
              ]}
            />
          </Col>
        </Row>
      </FlexibleCard>
      {agreementCount && analyticsDatasource && (
        <DynamicDashboard
          dashboard={"perrental"}
          dataSource={analyticsDatasource}
        />
      )}

      <FlexibleCard
        compact={true}
        extras={
  <div style={{ display: "flex", gap: 8 }}>
    <Button
      size="small"
      variant="outlined"
      color="purple"
      className="cursor-pointer"
      icon={<CalendarOutlined />}
      onClick={() => navigate(`/calendar?rentalId=${id}&rentalName=${rentInfo.name}&rentalType=${rentInfo.type}&rentalTotalUnits=${rentInfo.totalUnits}`)}
    >
      Check availability
    </Button>
    <Button
      size="small"
      variant="solid"
      color="purple"
      className="cursor-pointer"
      icon={<PlusOutlined />}
      onClick={() => formNavigator.agreement(id, -1, -1, {
        quantibleType: rentInfo.quantibleType,
        amount: rentInfo.defaultAmount || 0,
      })}
    >
      Add
    </Button>
  </div>
}
        cardTitle={getIcon("Agreements", true, {
          size: 30,
          title: constants["AGREEMENT_TITLE"] ?? "Agreements",
        })}
      >
        {activeTab == "agreements" && (
          <>
            <Filters
              dispatch={dispatch}
              filterPage="agreement"
              values={agreementFilters}
            />

            <TableView
              editableCallback={({ agreementId, rentalId, tenantId }) => {
                formNavigator.agreement(rentalId, tenantId, agreementId, {
                  quantibleType: rentInfo.quantibleType,
                });
              }}
              editShow={(record) => {
                if (record?.rentalAgreement?.eventId) {
                  return (
                    <Link to={`/event/${record?.rentalAgreement?.eventId}`}>
                      <Tag color={"blue"}>Open</Tag>
                    </Link>
                  );
                }
                return true;
              }}
              actionMenu={(record) => {
                return (
                  <ActionsMenu
                    actionRecord={record}
                    configs={[
                      {
                        label: (
                          <span className="flex items-center gap-2 text-red-500">
                            <DeleteOutlined /> Delete
                          </span>
                        ),
                        callback: async (record) => {
                          await deleteAgreement(record);
                          dispatch(clearRentInfo());
                          loadRentalInfo(dispatch, id);
                          loadRentalAnalytics(dispatch, id);
                        },
                        danger: true,
                        sectionEnd:true
                      },
                      {
                        label: (
                          <div
                            className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-gray-900">
                                Choose any config
                              </span>
                            </div>

                            <Select
                              className="w-[220px]"
                              size="middle"
                              placeholder="Select config"
                              options={invoiceConfigs.map((item) => ({
                                label: item.configName,
                                value: item.id,
                              }))}
                              value={selectedInvoiceConfig}
                              onChange={(val) => setSelectedInvoiceConfig(val)}

                            />
                          </div>
                        ),
                        callback: async (record) => {
                          await deleteAgreement(record);
                          dispatch(clearRentInfo());
                          loadRentalInfo(dispatch, id);
                          if(shouldShowAnalytics){
                          loadRentalAnalytics(dispatch, id);
                          }
                        },
                        danger: true,
                      },

                      {
                        label: (
                          <span className="flex items-center gap-2">
                            <FilePdfOutlined /> Download Invoice
                          </span>
                        ),
                        callback: (record) => {
                          if (selectedInvoiceConfig){
                          message.success("Getting Invoice Ready , You will be redirected to view mode and You can download from there")
                          generateInvoice(
                            record.rentalAgreement.id,
                            selectedInvoiceConfig,
                          );
                        }else {
                          message.error("No valid invoice config selected , Add it in invoice builder")
                        }

                        },
                      },
                      {
                        label: (
                          <span className="flex items-center gap-2">
                            <FilePdfOutlined /> Regenrate & Download Invoice
                          </span>
                        ),
                        callback: (record) => {
                          if(selectedInvoiceConfig){
                          generateInvoice(
                            record.rentalAgreement.id,
                            selectedInvoiceConfig,
                            true,
                          );
                          message.success("Getting Invoice Ready , You will be redirected to view mode and You can download from there")
                        }else {
                          message.error("No valid invoice config selected , Add it in invoice builder")
                        }
                        }
                      },
                    ]}
                    title={
                      <Tag className="ml-1" size="small">
                        <SettingOutlined />
                      </Tag>
                    }
                  />
                );
              }}
              pageLimit={pageLimit}
              dataKey={"id"}
              onPageChange={(pageNum) =>
                loadAgreements(dispatch, pageNum, pageLimit, agreementPages, id)
              }
              totalData={agreementCount}
              rowKey={(record) => {
                return `agreement-${record.id}`;
              }}
              expandable={{ expandedRowRender: config.agreementRowExpand }}
              data={agreementInfo}
              renderFunctions={config.agreementFieldsRender}
              onlyFields={config.agreementFields}
            />
          </>
        )}
      </FlexibleCard>
    </div>
  );
};

export default RentalView;
